import React, { useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";
import { clearAppCache } from "../../utils";

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface RoleData {
  RoleId: number;
  RoleName: string;
  roleMenuDatas: null;
}

interface UserRoleData {
  UserRoleId: number;
  UserId: number;
  MallId: number;
  MallName: string;
  TenantId?: number;
  roleData: RoleData;
}

interface UserRoleResponse {
  userRoleDatas: UserRoleData[];
  Code: number;
  Message: string;
}

interface MokaOutletItem {
  TenantId: number;
  TenantUserName: string | null;
  TenantPassword: string | null;
  OutletId: number;
  OutletName: string;
  Address: string;
  PhoneNumber: string | null;
  City: string | null;
  Province: string | null;
  PostalCode: string | null;
  IsConnected: boolean;
}

interface MokaOutletResponse {
  TotalRecord: number;
  AccessToken: string;
  RefreshToken: string;
  CreatedToken: number;
  ExpiredToken: number;
  MerchantCode: string;
  mokaOutletDatas: MokaOutletItem[];
  Code: number;
  Message: string;
}

declare const API_BASE_URL_TOKEN: string;
declare const API_BASE_URL_TOKEN_DEV: string;
declare const API_BASE_URL: string;
declare const API_BASE_MOKA: string;
declare const API_BASE_MOKA_TOKEN: string;

export default function TenantAccessLogin(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const isMokaFlow = Boolean(code);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [userRoles, setUserRoles] = useState<UserRoleData[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const navigate = useNavigate();

  React.useEffect(() => {
    const rememberMe = localStorage.getItem("remember_me") === "true";
    if (rememberMe) {
      const savedEmail = localStorage.getItem("user_email") || "";
      const savedPassword = localStorage.getItem("user_password") || "";
      setFormData({
        email: savedEmail,
        password: savedPassword,
        rememberMe: true,
      });
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError("");
  };

  const fetchMokaOutlets = async (): Promise<MokaOutletResponse> => {
    const response = await axios.get(
      `${API_BASE_URL}/moka/v1/getmokaoutlet?code=${code}`,
    );
    return response.data;
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError("");

    try {
      if (!formData.email || !formData.password) {
        throw new Error("Email and password fields must be filled");
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error("Please enter a valid email address");
      }

      const userRole = await fetchUserRole(formData.email, formData.password);

      if (userRole.Code === 6) {
        setError(userRole.Message);
        setIsLoading(false);
        return;
      }

      if (userRole.Code === 7) {
        await Swal.fire({
          title: "User Inactive",
          text: "Redirecting to activation form...",
          icon: "warning",
          showConfirmButton: false,
          timer: 1200,
          timerProgressBar: true,
          allowOutsideClick: false,
        });
        navigate("/Inactivate");
        setIsLoading(false);
      } else {
        if (userRole.userRoleDatas && userRole.userRoleDatas.length > 1) {
          setUserRoles(userRole.userRoleDatas);
          setShowRoleModal(true);
          setIsLoading(false);
        } else {
          await proceedLogin(userRole.userRoleDatas[0]);
        }
      }
    } catch (err) {
      let message = "An unexpected error occurred during login";

      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const serverMsg =
          err.response?.data?.message || err.response?.data?.error || "";
        message = status
          ? `Server error (${status}): ${serverMsg || "Unknown error"}`
          : `Network error: ${err.message}`;
      } else if (err instanceof Error) {
        message = err.message;
      }

      setError(message);
      setIsLoading(false);
    }
  };

  const handleRoleSelection = async () => {
    if (!selectedRoleId) {
      setError("Please select a role to continue");
      return;
    }

    setIsLoading(true);
    setShowRoleModal(false);

    try {
      const selectedRole = userRoles.find(
        (role) => role.UserRoleId.toString() === selectedRoleId,
      );
      if (selectedRole) {
        await proceedLogin(selectedRole);
      }
    } catch (err) {
      let message = "An unexpected error occurred during login";

      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const serverMsg =
          err.response?.data?.message || err.response?.data?.error || "";
        message = status
          ? `Server error (${status}): ${serverMsg || "Unknown error"}`
          : `Network error: ${err.message}`;
      } else if (err instanceof Error) {
        message = err.message;
      }

      setError(message);
      setIsLoading(false);
    }
  };

  const proceedLogin = async (selectedUserRole: UserRoleData) => {
    try {
      const userRoleId = selectedUserRole.UserRoleId.toString();

      // Fetch token dari prod dan dev secara paralel
      const [prodResult, devResult] = await Promise.allSettled([
        fetchLoginUser(formData.email, formData.password, userRoleId, API_BASE_URL_TOKEN),
        fetchLoginUser(formData.email, formData.password, userRoleId, API_BASE_URL_TOKEN_DEV),
      ]);

      const prodToken = prodResult.status === "fulfilled" ? prodResult.value.access_token : null;
      const devToken = devResult.status === "fulfilled" ? devResult.value.access_token : null;

      const isProductionEnv =
        window.location.hostname === "tenant.lippomallpuri.com" ||
        window.location.hostname === "tenantaccess.lippomalls.com" ||
        API_BASE_URL.includes("tenantaccessapi");

      if (isProductionEnv && !prodToken) {
        const err = prodResult.status === "rejected" ? prodResult.reason : new Error("Prod login failed");
        let errMsg = "Gagal menyambungkan ke server autentikasi produksi.";
        if (axios.isAxiosError(err)) {
          errMsg = err.response?.data?.error_description || err.response?.data?.error || err.message;
        } else if (err instanceof Error) {
          errMsg = err.message;
        }
        throw new Error(`Login Produksi Gagal: ${errMsg}`);
      }

      if (!prodToken && !devToken) {
        const err = devResult.status === "rejected" ? devResult.reason : (prodResult.status === "rejected" ? prodResult.reason : new Error("Login failed"));
        let errMsg = "Gagal mendapatkan token dari server.";
        if (axios.isAxiosError(err)) {
          errMsg = err.response?.data?.error_description || err.response?.data?.error || err.message;
        } else if (err instanceof Error) {
          errMsg = err.message;
        }
        throw new Error(`Login Gagal: ${errMsg}`);
      }

      const tenantId =
        selectedUserRole.TenantId != null
          ? selectedUserRole.TenantId.toString()
          : (selectedUserRole.roleData?.RoleName?.toUpperCase() === "HO" ? "0" : null);

      clearAppCache();
      const storage = formData.rememberMe ? localStorage : sessionStorage;

      if (prodToken) {
        storage.setItem("access_token", prodToken);
        storage.setItem("token", prodToken);
      }

      if (devToken) {
        storage.setItem("access_token_dev", devToken);
        storage.setItem("token_dev", devToken);
      }

      storage.setItem("user_email", formData.email);
      storage.setItem("user_password", formData.password);
      storage.setItem("mall_id", selectedUserRole.MallId.toString());
      storage.setItem("role_name", selectedUserRole.roleData.RoleName);
      const loginTenantIdVal =
        selectedUserRole.TenantId != null && selectedUserRole.TenantId !== 0
          ? selectedUserRole.TenantId.toString()
          : "0";
      storage.setItem("login_tenant_id", loginTenantIdVal);

      if (tenantId !== null) {
        storage.setItem("tenant_id", tenantId);
      } else {
        storage.removeItem("tenant_id");
      }

      if (formData.rememberMe) {
        localStorage.setItem("remember_me", "true");
        localStorage.setItem("user_email", formData.email);
        localStorage.setItem("user_password", formData.password);
      } else {
        localStorage.removeItem("remember_me");
        localStorage.removeItem("user_email");
        localStorage.removeItem("user_password");
      }

      // ── Moka flow: fetch outlet data then redirect to /thanks ──
      if (isMokaFlow) {
        const mokaData = await fetchMokaOutlets();

        sessionStorage.setItem(
          "moka_outlets",
          JSON.stringify(mokaData.mokaOutletDatas),
        );
        sessionStorage.setItem("moka_code", code ?? "");
        sessionStorage.setItem("moka_access_token", mokaData.AccessToken);
        sessionStorage.setItem("moka_refresh_token", mokaData.RefreshToken);
        sessionStorage.setItem(
          "moka_created_token",
          String(mokaData.CreatedToken),
        );
        sessionStorage.setItem(
          "moka_expired_token",
          String(mokaData.ExpiredToken),
        );
        sessionStorage.setItem("moka_password", String(formData.password));

        await Swal.fire({
          title: "Login Successful!",
          icon: "success",
          showConfirmButton: false,
          timer: 1200,
          timerProgressBar: true,
          allowOutsideClick: false,
        });

        navigate("/thanks");
      } else {
        await Swal.fire({
          title: "Login Successful!",
          text: "Redirecting to dashboard...",
          icon: "success",
          showConfirmButton: false,
          timer: 1200,
          timerProgressBar: true,
          allowOutsideClick: false,
        });

        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLoginUser = async (
    email: string,
    password: string,
    userroleid: string,
    baseTokenUrl: string,
  ): Promise<{ access_token: string }> => {
    const url = `${baseTokenUrl}/token`;
    const body = new URLSearchParams({
      grant_type: "password",
      username: email,
      password: password,
      userroleid: userroleid,
    });

    try {
      const response = await axios.post(url, body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      return response.data;
    } catch (error) {
      const err = error as {
        response?: { data?: { error_description?: string; error?: string } };
      };
      throw new Error(
        err.response?.data?.error_description ||
        err.response?.data?.error ||
        "Login failed",
      );
    }
  };

  const fetchUserRole = async (
    email: string,
    password: string,
  ): Promise<UserRoleResponse> => {
    const url = `${API_BASE_URL}/user/v1/getuserrole`;
    try {
      const response = await axios({
        url,
        method: "GET",
        timeout: 0,
        headers: { "X-Username": email, "X-Password": password },
      });
      if (response.status === 200) return response.data;
      throw new Error("Failed to fetch user roles.");
    } catch (error) {
      const err = error as { response?: { data?: { Message?: string } } };
      throw new Error(
        err.response?.data?.Message || "Failed to fetch user roles.",
      );
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoading) handleLogin();
  };

  const handleCancelRoleSelection = () => {
    setShowRoleModal(false);
    setSelectedRoleId("");
    setUserRoles([]);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex flex-col lg:flex-row">
      {/* ── Role selection modal ── */}
      {showRoleModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                Select Role
              </h2>
              <button
                onClick={handleCancelRoleSelection}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select your role
              </label>
              <div className="relative">
                <select
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                >
                  <option value="">-- Choose Role --</option>
                  {userRoles.map((role) => (
                    <option key={role.UserRoleId} value={role.UserRoleId}>
                      {role.roleData.RoleName} - {role.MallName}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <button
                  onClick={handleRoleSelection}
                  disabled={!selectedRoleId || isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
                >
                  {isLoading ? "Loading..." : "Continue"}
                </button>
                <button
                  onClick={handleCancelRoleSelection}
                  disabled={isLoading}
                  className="w-full bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 shadow-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Illustration (desktop) ── */}
      <div className="hidden lg:flex lg:w-2/5 relative bg-white items-center justify-center p-8 xl:p-12">
        <div className="w-full max-w-md">
          <img
            src="https://img.freepik.com/free-vector/mobile-login-concept-illustration_114360-83.jpg"
            alt="Login Illustration"
            className="w-full h-auto object-contain"
          />
        </div>
      </div>

      {/* ── Login form ── */}
      <div className="w-full lg:w-3/5 flex items-center justify-center p-4 sm:p-6 md:p-8 bg-white">
        <div className="w-full max-w-md mx-auto">
          <div className="lg:hidden text-center mb-6 sm:mb-8">
            <img
              src="https://img.freepik.com/free-vector/mobile-login-concept-illustration_114360-83.jpg"
              alt="Login Illustration"
              className="w-full h-auto mx-auto object-contain"
            />
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl border-2 border-gray-300">
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-2">
                Tenant Access
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Sign in to manage your Brand Moka
              </p>
            </div>

            {error && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs sm:text-sm break-words">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="space-y-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                  Your Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm sm:text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200"
                  placeholder="Enter your email"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm sm:text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200 pr-11 sm:pr-12"
                    placeholder="Enter your password"
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                <label className="flex items-center cursor-pointer group">
                  <input
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
                    disabled={isLoading}
                  />
                  <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                    Remember me
                  </span>
                </label>
                <button
                  type="button"
                  className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors text-left sm:text-right"
                  disabled={isLoading}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-lg flex items-center justify-center text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed hover:from-blue-700 hover:to-blue-800"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing you in...
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
